import { SchoolAccessContent, schoolMeetingUrl } from "./SchoolAccessDialog";
import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuthenticationStore } from "../../stores/auth";
import { canOpenIntroduction, purposeGuide } from "./signupPurpose";
import { guidePath, resolveHelpAction } from "./actions";
import { SignupPurposeCards } from "./SignupPurposeCards";
import { useOnboardingPreference } from "./useOnboardingPreference";

export function SignupIntroductionDialog({ user, onClose, onQuickStart }) {
  const { state, loading, saving, save, current } =
    useOnboardingPreference(user);
  const [purpose, setPurpose] = useState(null);
  const [step, setStep] = useState("purpose");
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  useEffect(() => {
    if (state) setPurpose(state.purpose);
  }, [state]);
  const guide = purposeGuide(purpose);
  const exploringSchool = purpose === "EXPLORE_FOR_SCHOOL";
  const continueFlow = async () => {
    await save({ purpose });
    if (current()) setStep("recommendation");
  };
  const dismiss = async () => {
    await save({ action: "DISMISS" });
    if (current()) onClose();
  };
  const startWork = async () => {
    await save({ action: "COMPLETE" });
    const latest = useAuthenticationStore.getState().user;
    if (!current() || !canOpenIntroduction(latest)) return;
    if (purpose === "CREATE_QUIZZES_RESOURCES") {
      const action = resolveHelpAction(
        { ...guide, action: "createQuiz" },
        latest,
      );
      onClose();
      // Use the guide's existing permission-checked quiz entry point.
      navigate(action?.path || "/");
    } else onQuickStart();
  };
  const exploreIndependently = async () => {
    await save({ action: "COMPLETE" });
    if (current()) onClose();
  };
  const openExample = () => {
    // Native link navigation keeps the user gesture/new tab and preserves this app.
    void save({ action: "COMPLETE" }).then(() => {
      if (current()) onClose();
    });
  };
  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      aria-labelledby="signup-introduction-title"
      sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
    >
      <DialogTitle id="signup-introduction-title">
        {step === "purpose"
          ? "What would you most like help with?"
          : "Choose where to start"}
      </DialogTitle>
      <DialogContent dividers>
        {step === "purpose" ? (
          <Stack spacing={2}>
            <Typography>
              Choose what you want to achieve. This only helps us suggest a
              starting point; it does not change your account access.
            </Typography>
            {loading ? (
              <Typography role="status">Loading your choice…</Typography>
            ) : (
              <SignupPurposeCards
                value={purpose}
                onChange={setPurpose}
                disabled={saving}
              />
            )}
          </Stack>
        ) : (
          <Stack spacing={2}>
            {exploringSchool ? (
              <>
                <Typography variant="h6">
                  Connect Fruitify to your school
                </Typography>
                <SchoolAccessContent />
              </>
            ) : (
              <>
                <Typography variant="h6">{guide?.title}</Typography>
                <Typography>{guide?.summary}</Typography>
                <Typography>
                  See a completed example, or try your own work. Examples use
                  sample data.
                </Typography>
                {purpose === "UNDERSTAND_CLASS_PERFORMANCE" && (
                  <Typography>
                    Alternatively, use Quick Start to create your own assignment
                    and submit an answer for feedback.
                  </Typography>
                )}
              </>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              {guide && (
                <Button
                  component="a"
                  href={guidePath(guide)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={openExample}
                  disabled={saving}
                  variant="outlined"
                >
                  {exploringSchool
                    ? "See a class-insights example"
                    : "See an example"}
                </Button>
              )}
              {exploringSchool ? (
                <Button
                  component="a"
                  href={schoolMeetingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={openExample}
                  disabled={saving}
                  variant="contained"
                >
                  Book a meeting
                </Button>
              ) : (
                <Button
                  onClick={startWork}
                  disabled={saving}
                  variant="contained"
                >
                  {purpose === "CREATE_QUIZZES_RESOURCES"
                    ? "Create a quiz"
                    : purpose === "SAVE_TIME_MARKING"
                      ? "Continue with Quick Start"
                      : "Create an assignment with Quick Start"}
                </Button>
              )}
            </Stack>
          </Stack>
        )}
        {step === "purpose" && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Skip for now to be reminded on a future sign-in. Dismiss to stop
            automatic reminders. You can always reopen Getting started from Help
            and tutorials.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        {step === "purpose" ? (
          <>
            <Button onClick={onClose}>Skip for now</Button>
            <Button onClick={dismiss} disabled={saving || loading}>
              Dismiss
            </Button>
            <Button
              variant="contained"
              onClick={continueFlow}
              disabled={!purpose || saving || loading}
            >
              Continue
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setStep("purpose")} disabled={saving}>
              Back
            </Button>
            <Button onClick={exploreIndependently} disabled={saving}>
              Explore for myself
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
